/**
 * @copyright 2024 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback } from 'react'

type FormValuesType = {
  [key: string]: any // eslint-disable-line  @typescript-eslint/no-explicit-any
}

export type ValidatorFn = (value: any, formValues?: FormValuesType) => string | undefined // eslint-disable-line  @typescript-eslint/no-explicit-any
export type OnValueChangeFn = (fieldName: string, value: any) => void // eslint-disable-line  @typescript-eslint/no-explicit-any

type ValidatorFns = {
  [key: string]: ValidatorFn
}

export type Errors = {
  [key: string]: string | undefined
}

/**
 * Stores the form values and errors and provides functions to update the form values and validate the form fields.
 * @param initialValues
 * @param validators
 * @returns [onValueChange, validateFields, errors, formValues, setValues]
 */
const useForm = <T>(
  initialValues: T,
  validators: ValidatorFns
): [OnValueChangeFn, () => Promise<T>, Errors, T, (values: T) => void] => {
  const [formValues, setFormValues] = React.useState<T>(initialValues)
  const [errors, setErrors] = React.useState<Errors>({})

  const onValueChange = useCallback(
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    (fieldName: string, value: any) => {
      setFormValues((prevFormValues) => {
        if (validators[fieldName]) {
          const error = validators[fieldName](value, prevFormValues as FormValuesType)
          setErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: error,
          }))
        }
        return {
          ...prevFormValues,
          [fieldName]: value,
        }
      })
    },
    [validators]
  )

  const validateFields = useCallback(async () => {
    const fieldErrors = {} as Errors
    let hasErrors = false
    const values = formValues as FormValuesType
    Object.keys(validators).forEach((fieldName) => {
      const error = validators[fieldName](values[fieldName], values)
      hasErrors = hasErrors || !!error
      fieldErrors[fieldName] = error
    })
    if (hasErrors) {
      setErrors(fieldErrors)
      return Promise.reject(fieldErrors)
    }
    return Promise.resolve(values as T)
  }, [formValues, validators])

  const setValues = useCallback(
    (values: T) => {
      setFormValues(values)
      const fieldValues = values as FormValuesType
      const fieldErrors = {} as Errors
      Object.keys(validators).forEach((fieldName) => {
        fieldErrors[fieldName] = validators[fieldName](fieldValues[fieldName], fieldValues)
      })
      setErrors(fieldErrors)
    },
    [validators]
  )

  return [onValueChange, validateFields, errors, formValues, setValues]
}

export default useForm
